import React, { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  Box,
  Stack,
  Typography,
  Avatar,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  CircularProgress,
  InputAdornment,
} from "@mui/material"
import StraightenIcon from "@mui/icons-material/Straighten"
import SaveIcon from "@mui/icons-material/Save"
import config from "config/app.config"

const API_KEY = config.APP_API_KEY
const TABLE_URL = config.APP_URL + "_table/custom_size_surcharges"

const getHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser"))
  const token = authUser ? authUser.session_token : ""
  return {
    Accept: "application/json",
    "Content-Type": "application/json",
    Authorization: token ? `Bearer ${token}` : "",
    "X-Api-Key": API_KEY,
  }
}

const POSITION_LABELS = {
  Base: "ΒΑΣΕΩΣ (Base)",
  Wall: "ΚΡΕΜΑΣΤΑ (Wall)",
  Tall: "ΟΛΟΣΩΜΑ (Tall)",
}

const DIM_LABELS = {
  W: "Width (W)",
  D: "Depth (D)",
  H: "Height (H)",
}

const CustomSizesTab = ({ onSnackbar }) => {
  const { t } = useTranslation()
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)

  const loadData = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(TABLE_URL, {
        headers: getHeaders(),
      })
      const json = await res.json()
      const data = json.resource || json
      setRows(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error loading surcharges:", err)
      onSnackbar?.({ open: true, message: t("Error loading surcharges"), severity: "error" })
    } finally {
      setLoading(false)
    }
  }, [onSnackbar, t])

  useEffect(() => {
    loadData()
  }, [loadData])

  const updatePrice = (id, value) => {
    setRows(prev =>
      prev.map(r => (r.id === id ? { ...r, price: value } : r))
    )
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload = rows.map(({ id, price }) => ({
        id,
        price: parseFloat(price) || 0,
      }))
      await fetch(TABLE_URL, {
        method: "PATCH",
        headers: getHeaders(),
        body: JSON.stringify({ resource: payload }),
      })
      onSnackbar?.({ open: true, message: t("Surcharges saved successfully"), severity: "success" })
    } catch (err) {
      console.error("Error saving surcharges:", err)
      onSnackbar?.({ open: true, message: t("Error saving surcharges"), severity: "error" })
    } finally {
      setSaving(false)
    }
  }

  // Group rows by position for display
  const grouped = {}
  rows.forEach(r => {
    if (!grouped[r.position]) grouped[r.position] = []
    grouped[r.position].push(r)
  })

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40, bgcolor: "#fef3c7", color: "#d97706" }}>
            <StraightenIcon sx={{ fontSize: 20 }} />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" fontWeight={600} color="#111827">
              {t("Custom Size Surcharges")}
            </Typography>
            <Typography variant="body2" color="#6b7280">
              {t("Surcharge applied when a custom (smaller) dimension is selected")}
            </Typography>
          </Box>
        </Stack>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving || loading}
          sx={{
            bgcolor: "#3b82f6",
            "&:hover": { bgcolor: "#2563eb" },
            borderRadius: 2,
            textTransform: "none",
          }}
        >
          {t("Save")}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8fafc" }}>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Position")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569" }}>{t("Dimension")}</TableCell>
                <TableCell sx={{ fontWeight: 600, color: "#475569", width: 160 }}>{t("Surcharge")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(grouped).map(([position, dims]) =>
                dims.map((row, idx) => (
                  <TableRow key={row.id} hover>
                    <TableCell>
                      {idx === 0 ? (
                        <Typography fontWeight={600}>
                          {t(POSITION_LABELS[position] || position)}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Typography>{t(DIM_LABELS[row.dimension] || row.dimension)}</Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={row.price}
                        onChange={(e) => updatePrice(row.id, e.target.value)}
                        InputProps={{
                          startAdornment: <InputAdornment position="start">{"\u20AC"}</InputAdornment>,
                        }}
                        sx={{
                          width: 130,
                          "& .MuiOutlinedInput-root": { borderRadius: 1.5 },
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  )
}

export default CustomSizesTab
